<template>
  <button
    class="flex h-10 flex-row items-center justify-center gap-3 rounded-full bg-primary-100 px-3 dark:bg-primary-700"
    @click="processSetCommandPaletteOpened(true)"
  >
    <FontAwesomeIcon :icon="['fal', 'search']" class="dark:text-gray-300" />
    <span class="text-gray-500 dark:text-white">{{ $t('global.search') }}</span>
    <span
      class="hidden text-gray-500 dark:text-gray-400 [.os-macos_&]:flex [.os-macos_&]:flex-row [.os-macos_&]:items-center [.os-macos_&]:gap-0.5"
    >
      <kbd>⌘</kbd>
      +
      <kbd>K</kbd>
    </span>
    <span
      class="hidden text-gray-500 not-[.os-macos_&]:flex not-[.os-macos_&]:flex-row not-[.os-macos_&]:items-center not-[.os-macos_&]:gap-1 dark:text-gray-400"
    >
      <kbd>Ctrl</kbd>
      +
      <kbd>K</kbd>
    </span>
  </button>

  <TransitionRoot :show="commandPaletteOpened" as="template" appear>
    <Dialog
      as="div"
      class="relative z-[500]"
      @close="processSetCommandPaletteOpened(false)"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-blue-100/75 transition-opacity dark:bg-black/75"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-[500] overflow-y-auto p-4 sm:p-6 md:p-20">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="divider-y mx-auto max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all dark:rounded-md dark:border dark:bg-slate-800 dark:text-white"
          >
            <Combobox v-slot="{ activeOption }" v-model="selectedResult">
              <div class="flex flex-row px-4">
                <div class="flex flex-1 flex-row items-center gap-2">
                  <FontAwesomeIcon
                    v-if="loading"
                    :icon="['fal', 'spinner-third']"
                    class="pointer-events-none h-5 w-5 shrink-0 animate-spin text-slate-400"
                    aria-hidden="true"
                    spin
                  />
                  <FontAwesomeIcon
                    v-else
                    :icon="['fal', 'magnifying-glass']"
                    class="pointer-events-none h-5 w-5 shrink-0 text-slate-400"
                    aria-hidden="true"
                  />
                  <ComboboxInput
                    id="search-input"
                    ref="searchInput"
                    class="h-12 flex-1 border-0 bg-transparent text-gray-800 placeholder-slate-300 focus:border-0 focus:outline-none sm:text-sm dark:text-white"
                    placeholder="Search..."
                    autocomplete="off"
                    :display-value="() => searchValue"
                    @change="searchValue = $event.target.value"
                  />
                </div>
                <div class="flex shrink-0 items-center">
                  <button
                    v-for="existingFilter in existingFilters"
                    :key="existingFilter.value"
                    class="size-7 h-full"
                    :class="{
                      'text-gray-300': !searchFilters.includes(
                        existingFilter.value
                      ),
                      'text-primary': searchFilters.includes(
                        existingFilter.value
                      )
                    }"
                    @mousedown.prevent
                    @click="clickFilter(existingFilter.value)"
                  >
                    <FontAwesomeIcon :icon="existingFilter.icon" size="lg" />
                  </button>
                </div>
              </div>
              <ComboboxOptions
                v-if="searchResult.length"
                class="flex divide-x divide-gray-200"
                as="div"
                static
                hold
              >
                <div
                  :class="[
                    'max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4',
                    activeOption && 'sm:h-96'
                  ]"
                >
                  <div
                    hold
                    class="-mx-2 flex flex-col gap-2 text-sm text-gray-700 dark:text-white"
                  >
                    <template v-for="result in searchResult" :key="result.id">
                      <div>
                        <h2 class="py-1 font-bold capitalize-first">
                          {{ $t('attributes.' + result.label) }}
                        </h2>
                        <ComboboxOption
                          v-for="option in result.results"
                          :key="option.id"
                          v-slot="{ active }"
                          :value="option"
                          as="template"
                        >
                          <div
                            :class="[
                              'group flex cursor-pointer flex-row items-center rounded-md p-2 text-ellipsis select-none',
                              active &&
                                'bg-gray-100 text-gray-900 dark:text-slate-700'
                            ]"
                          >
                            <span
                              class="inline-flex flex-1 flex-row items-center gap-x-2 truncate"
                            >
                              <span class="capitalize-first">
                                {{ option.title }}
                              </span>

                              <small
                                v-if="wikiCityOptionHasSubTitle(option, result)"
                                class="capitalize-firs ml-2 text-slate-600 dark:text-white"
                              >
                                {{ option.subtitle }}
                              </small>
                            </span>
                            <span
                              v-if="option.distance_in_kilometers"
                              class="shrink-0 rounded bg-primary px-1 text-xs text-contrast-primary"
                            >
                              {{ option.distance_in_kilometers }} km
                            </span>
                          </div>
                        </ComboboxOption>
                      </div>
                    </template>
                  </div>
                </div>
                <CommandPalettePreview
                  v-if="activeOption"
                  :active-option="activeOption"
                  @click-option="(option) => clickResult(option)"
                />
              </ComboboxOptions>

              <div
                v-if="searchValue !== '' && searchResult.length === 0"
                class="px-6 py-14 text-center text-sm sm:px-14"
              >
                <p class="mt-2 text-gray-500 dark:text-white">
                  {{ $t('global.no_result_found') }}
                </p>
              </div>
            </Combobox>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import apiClient from '@u/apiClient.js'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { mapGetters, mapMutations } from 'vuex'
import { debounce } from '@u/debounce.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import CommandPalettePreview from '@c/CommandPalette/component/CommandPalettePreview.vue'
import SearchGlobalController from '@/wayfinder/actions/App/Http/Controllers/SearchGlobal/SearchGlobalController'

export default {
  name: 'CommandPalette',
  components: {
    CommandPalettePreview,
    ComboboxOption,
    ComboboxOptions,
    ComboboxInput,
    Combobox,
    DialogPanel,
    TransitionChild,
    Dialog,
    TransitionRoot,
    FontAwesomeIcon
  },
  data() {
    return {
      longitude: null,
      latitude: null,
      loading: false,
      searchResult: [],
      abortController: null,
      searchFilters: ['dossier', 'invoicing'],
      selectedResult: null,
      existingFilters: [
        {
          value: 'dossier',
          icon: ['fal', 'folder']
        },
        {
          value: 'people',
          icon: ['fal', 'user']
        },
        {
          value: 'invoicing',
          icon: ['fal', 'euro-sign']
        },
        {
          value: 'city',
          icon: ['fal', 'building-columns']
        },
        {
          value: 'place',
          icon: ['fal', 'map-marker-alt']
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      commandPaletteOpened: 'globalSearch/getCommandPaletteOpened'
    }),
    searchValue: {
      get() {
        return this.$store.state.globalSearch.searchValue
      },
      set(value) {
        this.$store.commit('globalSearch/setSearchValue', value)
      }
    },
    isMac() {
      return document.documentElement.classList.contains('os-macos')
    }
  },
  watch: {
    searchValue(...args) {
      this.debouncedSearch(...args)
    },
    searchFilters: {
      handler(newValue) {
        this.debouncedSearch(this.searchValue)
        localStorage.setItem(
          'command-palette-filters',
          JSON.stringify(newValue)
        )
      },
      deep: true
    },
    commandPaletteOpened(newValue) {
      if (newValue) {
        this.$nextTick(() => this.$refs.searchInput.$el.select())
      }
    },
    selectedResult(newValue) {
      if (newValue !== null) {
        this.clickResult(newValue)
      }
    }
  },
  mounted() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude
          this.longitude = position.coords.longitude
        },
        () => {},
        { timeout: 10000 }
      )
    }
    const saved = localStorage.getItem('command-palette-filters')
    if (saved) {
      try {
        this.searchFilters = JSON.parse(saved)
      } catch {
        // ignore invalid stored filters
      }
    }
    window.addEventListener('keydown', this.handleKeyDown)
  },
  created() {
    this.debouncedSearch = debounce((newValue) => {
      if (newValue && newValue.length >= 3) {
        this.loading = true
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        apiClient
          .route(SearchGlobalController.get(), {
            signal: this.abortController.signal,
            params: {
              terms: newValue,
              latitude: this.latitude,
              longitude: this.longitude,
              search: this.searchFilters
            }
          })
          .then((response) => {
            this.searchResult = response.data
          })
          .catch(() => {})
          .finally(() => (this.loading = false))
      } else if (!newValue || newValue.length === 0) {
        this.searchResult = []
      }
    }, 500)
  },
  beforeUnmount() {
    if (this.abortController) {
      this.abortController.abort()
    }
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    ...mapMutations({
      processSetCommandPaletteOpened: 'globalSearch/setCommandPaletteOpened'
    }),
    isResultType(type, result) {
      return result.type === type
    },
    wikiCityOptionHasSubTitle(option, result) {
      return (
        this.isResultType('App\\Models\\WikiCity', result) &&
        option.subtitle &&
        option.subtitle.length
      )
    },
    clickResult(result) {
      this.$router.push(result.router)
      this.processSetCommandPaletteOpened(false)
    },
    clickFilter(value) {
      this.$h.toggleElementInArray(this.searchFilters, value)
    },
    handleKeyDown(e) {
      const isCorrectCombo =
        (this.isMac && e.metaKey && e.key === 'k') ||
        (!this.isMac && e.ctrlKey && e.key === 'k')
      if (isCorrectCombo) {
        e.preventDefault()
        this.processSetCommandPaletteOpened(true)
      }
    }
  }
}
</script>

<style></style>
